import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { difference, isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { BacksolveSourceRow, PublicCompanyAdjustmentStatus, SecurityModel } from 'api';
import { companyOverviewTabUrl } from 'common/config/urls';
import { BACKSOLVE, OPM, SCENARIO_METHODS_BACKSOLVE, WATERFALL } from 'common/constants/allocations';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import {
  MARKET_ADJUSTMENT_REQUIREMENT,
  VALUATIONS_MARKET_ADJUSTMENT_ADD,
  VALUATIONS_MARKET_ADJUSTMENT_REMOVE,
  VALUATIONS_SLUG,
} from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { CapTable } from 'common/types/allocation';
import { CapTableInfo } from 'common/types/capTable';
import { MeasurementDate } from 'common/types/measurementDate';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { CustomCompactContainer, CustomContainer } from 'components';
import GridButtonWithTooltip from 'components/ActionButtons/GridButtonWithTooltip';
import { LayoutContext } from 'context';
import BacksolveContext, { BacksolveContextTypes } from 'context/BacksolveContext';
import { useExportSignal } from 'features/pdfExport';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getValidSecurities } from 'pages/CapTable/cap-table/utilities';
import BacksolveSummaryTable from 'pages/Valuations/approaches/backsolveApproach/BacksolveSummaryTable/BacksolveSummaryTable';
import BacksolveTable from 'pages/Valuations/approaches/backsolveApproach/BacksolveTable/BacksolveTable';
import {
  ALLOCATION_METHOD_ALIAS,
  BACKSOLVE_TABLE_NAMES,
  CAP_TABLE_ALIAS,
  VALUATION_APPROACH_GPC_ALIAS,
} from 'pages/Valuations/approaches/backsolveApproach/constants';
import MarketAdjustmentLedger from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/MarketAdjustmentLedger';
import { AS_OF_DATE_ALIAS } from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/constants';
import MarketAdjustmentTable from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentTable/MarketAdjustmentTable';
import OPMInputTable, {
  getRiskFreeRateKey,
} from 'pages/Valuations/approaches/backsolveApproach/OPMInputTable/OPMInputTable';
import SecurityTable from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/SecurityTable';
import { TARGET_VALUE_ALIAS } from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/util/constants';
import getDefaultBasketSecurity from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/util/getDefaultBasketSecurity';
import getRowConfig from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/util/getRowConfig';
import { SourceTableData } from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/customParser/types';
import customValidations from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/customValidations';
import { getCellByCustomKey } from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/getCellByAlias';
import normalizeSourceRowsOrder from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/normalizeSourceRowsOrder';
import resetSourceRowsSheet from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/resetSourceRowsSheet';
import {
  SOURCE_ROW_DESCRIPTIVE_ALIASES,
  SOURCE_ROW_FIELD_ALIASES,
  SOURCE_TOTALS_ROW_TARGET,
} from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/sheetAliases';
import SourceRowsTable from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/SourceRowsTable';
import useCalculateEquityValue from 'pages/Valuations/approaches/backsolveApproach/useCalculateEquityValue';
import { BACKSOLVE_APPROACH, PdfExportTargets, PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import { useApproachRegistry } from 'pages/ValuationsAllocation/context/ApproachRegistryContext';
import { useValuationNotesCache } from 'pages/ValuationsAllocation/hooks';
import { getApproachTableName } from 'pages/ValuationsAllocation/util';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import CapatableService from 'services/captable';
import { useTableValidation } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import AddActionButton from './AddActionButton/AddActionButton';
import getBacksolveTableRowConfig from './BacksolveTable/util/rowconfig';
import calculateWeightedPerShareValues from './calculateWeightedPerShareValue';
import useAddSourceDataRow from './hooks/useAddSourceDataRow';
import { useMarketAdjustmentTableChange } from './hooks/useMarketAdjustmentTableChange';
import useMutualFundMarksSourceRows from './hooks/useMutualFundMarksSourceRows';
import useSecondaryTransactionsSourceRows from './hooks/useSecondaryTransactionsSourceRows';
import {
  BackSolveColumn,
  BacksolveProps,
  BackSolveSpreadsheets,
  LedgerData,
  SecuritiesResponse,
  ValuationApproachBackSolveExtended,
} from './types';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const {
  BACKSOLVE: BACKSOLVE_TABLE,
  BACKSOLVE_SUMMARY,
  MARKET_ADJUSTMENT,
  OPM_INPUTS,
  SECURITIES,
} = BACKSOLVE_TABLE_NAMES;

const VALUATION_BACKSOLVE_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${BACKSOLVE_TABLE}-container`;
const VALUATION_BACKSOLVE_SECURITES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${SECURITIES}-container`;
const VALUATION_BACKSOLVE_OPM_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${OPM_INPUTS}-container`;
const VALUATION_BACKSOLVE_SUMMARY_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${BACKSOLVE_SUMMARY}-container`;
const VALUATION_BACKSOLVE_MARKET_ADJUSTMENT_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${MARKET_ADJUSTMENT}-container`;

export const getOpmData = (spreadsheets: BackSolveSpreadsheets): ICell[] => {
  const opmCells = spreadsheets.opmInputSheet.cells;
  // Maturity, volatility, risk-free rate, and backsolve date
  return [opmCells!.B2, opmCells!.B5, opmCells!.B6, opmCells!.B1];
};

export const getEditableSecurityValues = (securities: { security: string; shares: string }[]) =>
  securities.map(({ security, shares }) => ({ security, shares }));

// ST/MFM: Target Value stays linked to Source Rows only while the basket has a single security.
const syncSecurityTargetValueLinkage = (tableData: {
  sourceRowsSheetName?: string;
  isTargetValueReadOnly?: boolean;
}, basketLength: number) => {
  // eslint-disable-next-line no-param-reassign
  tableData.isTargetValueReadOnly = Boolean(tableData.sourceRowsSheetName) && basketLength <= 1;
};

const Backsolve = ({
  spreadsheets,
  onChange: onWorkbookChange,
  workbook,
  approaches,
  capTableList,
  approach,
}: BacksolveProps) => {
  const {
    resetConfiguration,
    marketDate,
    measurementDate,
    notesInApproach,
    setNotesInApproach,
    updateApproachesScenarioMethods,
    backsolveAttributes,
    publicCompsAttrs,
    registerApproachFormat,
  } = useContext(ValuationContext);
  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE });

  const [marketAdjustment, setMarketAdjustment] = useState<any>();

  const { viewOnlyUser, isExportMode } = useContext(LayoutContext);

  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const companyOverviewUrl = firmSlugParam && companySlugParam
    ? companyOverviewTabUrl(firmSlugParam, companySlugParam)
    : undefined;

  const approachPanelId = approach.panelId;
  const { notes, setNotes, notesHasChanged, setNotesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { notes: cachedNotes } = useValuationNotesCache({
    cachedNotes: notesInApproach,
    notes,
    notesHasChanged,
    panelId: approachPanelId,
    setCachedNotes: setNotesInApproach,
    setNotes,
    setNotesHasChanged,
  });
  const { isDisabled } = spreadsheets.backsolveSheet.tableData as unknown as { isDisabled: boolean };

  const backsolveType = approach.valuations_approach_backsolve?.backsolve_type;
  const isSourceRowsEnabled
    = backsolveType === 'secondary_transactions' || backsolveType === 'mutual_fund_marks';

  const getSourceRows = useCallback(
    () => normalizeSourceRowsOrder(approach.valuations_approach_backsolve?.source_rows ?? []),
    [approach]
  );

  const resetSourceRowsSheetWithRows = useCallback(
    (rows: BacksolveSourceRow[]) => {
      const normalizedRows = normalizeSourceRowsOrder(rows);
      if (approach.valuations_approach_backsolve) {
        // eslint-disable-next-line no-param-reassign
        approach.valuations_approach_backsolve.source_rows = normalizedRows;
      }
      const { sourceRowsSheet } = spreadsheets;
      if (!sourceRowsSheet) {
        return;
      }
      resetSourceRowsSheet({
        sourceRowsSheet,
        rows: normalizedRows,
        isDisabled,
        companyOverviewUrl,
        fieldAttributes: backsolveAttributes?.backsolveApproachAttrs,
      });
      resetConfiguration?.();
    },
    [
      approach,
      spreadsheets,
      isDisabled,
      companyOverviewUrl,
      backsolveAttributes?.backsolveApproachAttrs,
      resetConfiguration,
    ]
  );

  const { validateCells } = useTableValidation();

  // Approach Registry: register this backsolve if it depends on a PC
  const { stableIds, dependencyGraph } = useApproachRegistry();

  const { register: registerNode, unregister: unregisterNode } = dependencyGraph;
  const { getOrCreateId } = stableIds;


  const { calculateEquityValue, isLoading } = useCalculateEquityValue();

  const hasGpcApproach = useMemo(
    () => approaches.filter(({ approach_type }) => approach_type === PUBLIC_COMPANIES).length,
    [approaches]
  );

  useEffect(() => {
    if (!isSourceRowsEnabled || !spreadsheets.sourceRowsSheet || !companyOverviewUrl) {
      return;
    }
    if ((spreadsheets.sourceRowsSheet.tableData as unknown as SourceTableData).companyOverviewUrl === companyOverviewUrl) {
      return;
    }
    resetSourceRowsSheetWithRows(getSourceRows());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceRowsEnabled, companyOverviewUrl, spreadsheets.sourceRowsSheet]);

  const backsolveStableId = useMemo(() => getOrCreateId(approachPanelId), [getOrCreateId, approachPanelId]);

  const getSelectedCapTable = useCallback(
    (capTableId: string | number) => {
      if (Array.isArray(capTableList)) {
        return capTableList?.find(({ id }) => id?.toString() === capTableId?.toString());
      }
    },
    [capTableList]
  );

  const getAsOfDate = useCallback(
    () =>
      // if as_of_date is not set it will be an empty string, so we use the || check to default to the marketDate
      spreadsheets.backsolveSheet.tableData.approach.valuations_approach_backsolve.as_of_date || marketDate,
    [spreadsheets, marketDate]
  );

  const getAppliedMethodologies = useCallback(() => {
    const [, ...tmpApppliedMethodologies] = spreadsheets.backsolveSheet.columns;
    return tmpApppliedMethodologies;
  }, [spreadsheets]);

  useEffect(() => {
    const tabId = getApproachTableName({ approach: approach as any });
    registerApproachFormat?.(tabId, format, formatDispatch);
  }, [approach, format, formatDispatch, registerApproachFormat]);

  const checkIfIsOpm = useCallback(() => {
    const methods = getAppliedMethodologies();

    return methods.some((x: any) => Number(x.allocation_method) === Number(OPM));
  }, [getAppliedMethodologies]);

  // flag to hide and display the opm inputs table
  const [isOpm, setOpm] = useState(checkIfIsOpm());

  const getApproachByPanelId = useCallback(
    (id: string | number) => {
      if (!id) {
        return undefined;
      }
      return approaches
        .filter(({ approach_type }) => approach_type === PUBLIC_COMPANIES)
        .find(
          approachCandidate =>
            approachCandidate.panelId === id ||
            (approachCandidate.valuations_approach_gpc?.id &&
              approachCandidate.valuations_approach_gpc.id?.toString() === id.toString())
        );
    },
    [approaches]
  );

  const [gpcApproach, setGpcApproach] = useState(
    getApproachByPanelId(approach.valuations_approach_backsolve.gpc_approach ?? '')
  );

  // this useEffect hooks is meant to update the default gpc approach in the case the market date
  // changes. If the component has mounted at the time of the change, we need something
  // to pick up on that and update the gpc approach.
  useEffect(() => {
    const tmpGpcApproach = getApproachByPanelId(approach.valuations_approach_backsolve.gpc_approach ?? '');
    const compared = gpcApproach === tmpGpcApproach;
    if (!compared && marketDate) {
      setGpcApproach(tmpGpcApproach);
    }
  }, [marketDate, approach, getApproachByPanelId, gpcApproach]);

  const [selectedCapTable, setSelectedCapTable] = useState(
    getSelectedCapTable(spreadsheets.backsolveSheet.cells?.capTableSelection?.value as string)
  );

  useEffect(() => {
    if (capTableList?.length > 0) {
      setSelectedCapTable(getSelectedCapTable(spreadsheets.backsolveSheet.cells?.capTableSelection?.value as string));
    }
  }, [capTableList, getSelectedCapTable, spreadsheets]);

  const [prevOpmData, setPrevOpmData] = useState();
  const [prevEditableSecurityValues, setPrevEditableSecurityValues] = useState();
  const [prevTargetValue, setPrevTargetValue] = useState<any>();
  const [securityList, setSecurityList] = useState<SecurityModel[]>();
  // maturity|date key whose risk free rate fetch came back empty, so the OPM table's 0 is final.
  const [emptyRiskFreeRateKey, setEmptyRiskFreeRateKey] = useState<string | null>(null);

  const captableChange = useCallback(
    (capTable: CapTable) => {
      const securityCells = spreadsheets.securitySheet.cells;
      if (!securityCells) {
        return;
      }
      Object.values(securityCells).forEach(cell => {
        if (cell.alias === 'security') {
          onWorkbookChange(cell, '');
        }
      });
      const updatedApproach = { ...approach };
      const firstAppliedMethodology = updatedApproach.valuations_approach_backsolve.applied_methodologies[0];
      firstAppliedMethodology.cap_table = capTable.id as number;

      const updatedRowConfig = getBacksolveTableRowConfig({ captableList: capTableList, approach: updatedApproach });
      spreadsheets.backsolveSheet.reset({
        rowConfig: updatedRowConfig,
      });
      resetConfiguration?.();
    },
    [onWorkbookChange, spreadsheets, resetConfiguration, capTableList, approach]
  );

  useEffect(() => {
    const svc = new CapatableService();
    if (selectedCapTable) {
      svc.getCaptableById<SecuritiesResponse>(selectedCapTable.id!).then(response => {
        setSecurityList(response.securities);
      });
    }
  }, [selectedCapTable]);

  // Auto-select the cap table security with the latest investment date for new ST/MFM approaches
  useEffect(() => {
    if (!isSourceRowsEnabled || !securityList || securityList.length === 0) {
      return;
    }
    const basket = approach.valuations_approach_backsolve.securities_basket?.basket ?? [];
    const hasSelection = basket.some((s: any) => s.security && s.security !== '');
    if (hasSelection) {
      return;
    }

    const latestSecurity = getDefaultBasketSecurity(securityList);
    if (latestSecurity && basket[0]) {
      basket[0].security = String(latestSecurity.id);
      basket[0].shares = '1';
      const { securitySheet } = spreadsheets;
      securitySheet.reset({ tableData: securitySheet.tableData, rowConfig: getRowConfig(basket) });
      resetConfiguration?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceRowsEnabled, securityList]);

  useSecondaryTransactionsSourceRows({ backsolveType, measurementDate, getSourceRows, resetSourceRowsSheetWithRows });
  useMutualFundMarksSourceRows({ backsolveType, measurementDate, getSourceRows, resetSourceRowsSheetWithRows });

  useEffect(() => {
    if (!isSourceRowsEnabled) {
      return;
    }
    const currentRows = getSourceRows();
    const normalizedRows = normalizeSourceRowsOrder(currentRows);
    const needsReorder = normalizedRows.some(
      (row, index) => row.source_record_id !== currentRows[index]?.source_record_id,
    );
    if (needsReorder) {
      resetSourceRowsSheetWithRows(normalizedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceRowsEnabled]);

  // Validate editable source-row cells on load and after sheet re-evaluation (e.g. add
  // row) so required-field and weighting errors show without needing a save attempt first.
  useEffect(() => {
    if (!isSourceRowsEnabled) {
      return;
    }
    const { sourceRowsSheet } = spreadsheets;
    if (!sourceRowsSheet?.cells) {
      return;
    }
    const editableSourceRowCells = Object.values(sourceRowsSheet.cells).filter(
      (c: ICell) =>
        SOURCE_ROW_FIELD_ALIASES.some(alias => c.alias?.startsWith(alias)) && !c.readOnly
    );
    if (editableSourceRowCells.length === 0) {
      return;
    }
    validateCells({
      cellsToValidate: editableSourceRowCells,
      useOriginalCell: true,
      customValidations,
      parsedColumns: sourceRowsSheet.cells,
      tableData: sourceRowsSheet.tableData,
    });
    sourceRowsSheet.updateAllCellsDisplayConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceRowsEnabled, spreadsheets.sourceRowsSheet?.cells]);

  const getSecurityRows = useCallback(
    () => approach.valuations_approach_backsolve.securities_basket.basket,
    [approach]
  );

  const getAllocationMethods = useCallback(
    () => approach.valuations_approach_backsolve.applied_methodologies,
    [approach]
  );

  const securityRows = useMemo(() => {
    if (approach) {
      return getSecurityRows();
    }
    return [];
  }, [getSecurityRows, approach]);

  const allocationMethods = useMemo(() => {
    if (approach) {
      return getAllocationMethods();
    }
    return [];
  }, [getAllocationMethods, approach]);

  const getScenarioMethod = useCallback(
    () => spreadsheets.backsolveSheet.cells?.allocationMethod?.value,
    [spreadsheets]
  );

  const allocation = useMemo(() => approach.allocationId, [approach.allocationId]);

  const areAppliedMethodologiesValid = useCallback(() => {
    const methodologies = (spreadsheets.backsolveSheet.tableData as any).approach?.valuations_approach_backsolve
      ?.applied_methodologies;

    const duplicatedAllocationMethods = methodologies.filter(
      (m: any) => methodologies.filter((x: any) => x.allocation_method === m.allocation_method)?.length > 1
    );

    return duplicatedAllocationMethods.length === 0;
  }, [spreadsheets]);

  const arePresentShareValuesReady = useMemo(() => {
    const methodologies = (spreadsheets.backsolveSheet.tableData as any).approach?.valuations_approach_backsolve
      ?.applied_methodologies;
    if (methodologies) {
      return methodologies.every((m: any) => m.present_share_values.length > 0);
    }
    return false;
  }, [spreadsheets]);

  const getTargetValue = useCallback(() => {
    const securityCells = spreadsheets.securitySheet.cells;
    if (securityCells) {
      const linkedTargetCell = getCellByCustomKey(securityCells, SOURCE_TOTALS_ROW_TARGET);
      if (linkedTargetCell) {
        return linkedTargetCell.value;
      }
    }
    const rowIndex = (spreadsheets.securitySheet.tableData as { securitiesBasket: { basket: unknown[] } }).securitiesBasket
      .basket.length + 2;
    return (spreadsheets.securitySheet.cells as Record<string, { value?: unknown }>)?.[`D${rowIndex}`]?.value;
  }, [spreadsheets]);

  useEffect(() => {
    if (selectedCapTable && areAppliedMethodologiesValid() && !isEmpty(securityList) && !arePresentShareValuesReady) {
      const data = {
        captable: selectedCapTable,
        scenarioType: BACKSOLVE,
        allocation,
        appliedMethodologies: getAppliedMethodologies(),
        opmData: getOpmData(spreadsheets),
        securityRows,
        scenarioMethod: getScenarioMethod(),
        targetValue: getTargetValue(),
        prevOpmData,
        editableLedgerCells: getEditableSecurityValues(securityRows),
        prevEditableLedgerCells: prevEditableSecurityValues,
        prevTargetValue,
        securityList,
        spreadsheets,
        onCellsChanged: onWorkbookChange,
      };

      calculateEquityValue(data);
    }
    // disabling because we only want this to load when the page does, or when the security list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityList, arePresentShareValuesReady]);

  const recalculateEquityValue = useCallback(
    async (captable: CapTable, basket = securityRows, targetValue = null) => {
      if (captable && areAppliedMethodologiesValid()) {
        const data = {
          captable: captable.id,
          scenarioType: BACKSOLVE,
          allocation,
          appliedMethodologies: getAppliedMethodologies(),
          marketDate: getAsOfDate(),
          opmData: getOpmData(spreadsheets),
          securityRows: basket,
          scenarioMethod: getScenarioMethod(),
          targetValue: targetValue ?? getTargetValue(),
          prevOpmData,
          editableLedgerCells: getEditableSecurityValues(basket),
          prevEditableLedgerCells: prevEditableSecurityValues,
          prevTargetValue,
          securityList,
          spreadsheets,
          onCellsChanged: onWorkbookChange,
        };
        const calculatedEquityValue = await calculateEquityValue(data);
        return calculatedEquityValue || {};
      }
    },
    [
      areAppliedMethodologiesValid,
      allocation,
      getScenarioMethod,
      getAppliedMethodologies,
      getAsOfDate,
      onWorkbookChange,
      spreadsheets,
      getTargetValue,
      calculateEquityValue,
      securityList,
      securityRows,
      prevTargetValue,
      prevOpmData,
      prevEditableSecurityValues,
    ]
  );

  // Use custom hook for market adjustment table changes
  const { onMarketAdjustmentTableChange, isLoadingMarketAdjustment } = useMarketAdjustmentTableChange({
    gpcApproach,
    approach,
    setMarketAdjustment,
    backsolveAttributes: backsolveAttributes || undefined,
    publicCompsAttributes: publicCompsAttrs || undefined,
    format,
  });

  const onChange = useCallback(
    (cell: any, value: any, skipEquityRecalculation = false) => {
      // Handle market adjustment table changes
      if (cell.alias === AS_OF_DATE_ALIAS) {
        onMarketAdjustmentTableChange(value);
      }

      // changes from market adjustment table should not have the equity value recalculated
      if (cell.alias === VALUATION_APPROACH_GPC_ALIAS && value) {
        // should probably be a panel id
        setGpcApproach(getApproachByPanelId(value));
      }
      if (cell.alias === TARGET_VALUE_ALIAS) {
        setPrevTargetValue(cell.value);
        // calculate pershare values, total values, and equity values
      }
      let capTable = selectedCapTable;
      if (cell.alias === CAP_TABLE_ALIAS) {
        capTable = getSelectedCapTable(value);
        if (capTable && capTable.id !== selectedCapTable?.id) {
          setSelectedCapTable(capTable);
          captableChange(capTable);
        }
      }
      const sourceRowsSheetName = spreadsheets.sourceRowsSheet?.name;
      const isSourceRowsCell = Boolean(sourceRowsSheetName) && cell.sheet?.name === sourceRowsSheetName;
      const isDescriptiveSourceRowEdit
        = isSourceRowsCell && SOURCE_ROW_DESCRIPTIVE_ALIASES.some(alias => cell.alias?.startsWith(alias));
      onWorkbookChange(cell, value);
      if (isSourceRowsCell && approach.valuations_approach_backsolve && spreadsheets.sourceRowsSheet) {
        // eslint-disable-next-line no-param-reassign
        approach.valuations_approach_backsolve.source_rows = (
          spreadsheets.sourceRowsSheet.tableData as unknown as SourceTableData
        ).sourceRows;
      }
      if (cell.alias === ALLOCATION_METHOD_ALIAS) {
        setOpm(checkIfIsOpm());
        updateApproachesScenarioMethods();
      }
      // backsolve — skip for descriptive source-row edits (Title/Date/Source/Size), which
      // cannot change the Target Price Per Share that drives the equity value calculation.
      if (!skipEquityRecalculation && !isDescriptiveSourceRowEdit) {
        recalculateEquityValue(capTable as CapTable);
      }
    },
    [
      onMarketAdjustmentTableChange,
      selectedCapTable,
      onWorkbookChange,
      checkIfIsOpm,
      getApproachByPanelId,
      getSelectedCapTable,
      captableChange,
      recalculateEquityValue,
      updateApproachesScenarioMethods,
      spreadsheets.sourceRowsSheet,
      approach,
    ]
  );

  const [hasMarketAdjustment, setHasMarketAdjustment] = useState(
    approach.valuations_approach_backsolve.adjust_for_market
  );

  useEffect(() => {
    // Register if market adjustment is enabled and PC approach is set
    if (hasMarketAdjustment && gpcApproach && backsolveStableId) {
      const sourceOfTruthStableId = getOrCreateId(gpcApproach.panelId!);
      registerNode({
        approachId: backsolveStableId,
        approachType: BACKSOLVE_APPROACH,
        panelId: approachPanelId,
        sourceOfTruthType: PUBLIC_COMPANIES,
        sourceOfTruthStableId,
      });
    } else {
      // Unregister if market adjustment is disabled
      unregisterNode(backsolveStableId);
    }
  }, [
    approachPanelId,
    hasMarketAdjustment,
    gpcApproach,
    backsolveStableId,
    registerNode,
    unregisterNode,
    getOrCreateId,
  ]);

  useEffect(() => {
    if (approach.valuations_approach_backsolve.market_adjustment) {
      setMarketAdjustment(approach.valuations_approach_backsolve.market_adjustment);
    }
  }, [approach]);

  const changeMarketAdjustment = useCallback(() => {
    const { valuations_approach_backsolve } = approach;
    // eslint-disable-next-line max-len
    valuations_approach_backsolve.adjust_for_market = !valuations_approach_backsolve.adjust_for_market;
    if (!valuations_approach_backsolve.adjust_for_market) {
      // eslint-disable-next-line no-param-reassign
      valuations_approach_backsolve.gpc_approach = null;
      (valuations_approach_backsolve.public_companies_status ?? []).forEach((status: PublicCompanyAdjustmentStatus) => {
        // eslint-disable-next-line no-param-reassign
        status.is_deleted = true;
      });
      setMarketAdjustment(approach.valuations_approach_backsolve.market_adjustment);
    }
    setHasMarketAdjustment(valuations_approach_backsolve.adjust_for_market);
  }, [approach]);

  // this is used to set and reset the enterprise value in the market adjustment table
  // as that cell is used to update the panel and the summary. When we remove a market adjustment
  // this erases the market adjustment cell and thus makes the enterprise value in both tables
  // equal.
  useEffect(() => {
    let value = 0;
    if (hasMarketAdjustment) {
      value = Number(marketAdjustment);
    }
    // only fire if spreadsheets.marketAdjustmentSheet.cells.MarketAdjustment is different from the value
    const cellValue = Number(spreadsheets.marketAdjustmentSheet.cells?.MarketAdjustment.value);

    if (cellValue !== value) {
      onChange(spreadsheets.marketAdjustmentSheet.cells?.MarketAdjustment as ICell, value);
    }
  }, [hasMarketAdjustment, marketAdjustment, onChange, spreadsheets.marketAdjustmentSheet.cells?.MarketAdjustment]);

  const updateMethodologies = (currentAllocationMethods: BackSolveColumn[]) =>
    currentAllocationMethods.forEach(item => {
      // eslint-disable-next-line no-param-reassign
      item.weight = currentAllocationMethods.length === 1 ? '1' : item.weight;
      // eslint-disable-next-line no-param-reassign
      item.isDeleteableColumn = currentAllocationMethods.length > 1;
    });

  const addAllocationMethod = useCallback(async () => {
    const defaultCapTable = capTableList?.length ? capTableList.find(ct => ct.is_primary)?.id : undefined;

    const { backsolveSheet } = spreadsheets;
    const { applied_methodologies: currentAllocationMethods } = (backsolveSheet.tableData as any).approach
      .valuations_approach_backsolve;
    currentAllocationMethods.push({
      allocation_method: WATERFALL.toString(),
      cap_table: selectedCapTable?.id ?? defaultCapTable,
      weight: '',
      maturity: null,
      volatility: null,
      present_share_values: [],
    });

    updateMethodologies(currentAllocationMethods);

    backsolveSheet.reset({
      tableData: backsolveSheet.tableData,
      columns: [[], ...currentAllocationMethods],
    });
    resetConfiguration?.();
    updateApproachesScenarioMethods();
  }, [resetConfiguration, spreadsheets, capTableList, selectedCapTable, updateApproachesScenarioMethods]);

  const addSecurity = useCallback(async () => {
    const { securitySheet } = spreadsheets;
    const tableData = securitySheet.tableData as {
      securitiesBasket: { basket: unknown[] };
      sourceRowsSheetName?: string;
      isTargetValueReadOnly?: boolean;
    };
    const { basket } = tableData.securitiesBasket;
    basket.push({
      security: '',
      shares: '',
      value: '',
      per_share: '',
    });

    syncSecurityTargetValueLinkage(tableData, basket.length);
    await securitySheet.reset({
      tableData,
      rowConfig: getRowConfig(basket),
    });
    // reset() does not re-parse; evaluate so Target Value unlinks when basket length > 1.
    await workbook.evaluateSpreadsheets(Object.values(spreadsheets).filter(Boolean));
    resetConfiguration?.();
  }, [resetConfiguration, spreadsheets, workbook]);

  const addSourceDataRow = useAddSourceDataRow({
    approach,
    getSourceRows,
    resetSourceRowsSheetWithRows,
  });

  const deleteSecurity = useCallback(
    async (securityIndex: number) => {
      const { securitySheet } = spreadsheets;
      const tableData = securitySheet.tableData as {
        securitiesBasket: { basket: any[]; target_value?: unknown };
        sourceRowsSheetName?: string;
        isTargetValueReadOnly?: boolean;
      };
      const { basket, target_value: tv } = tableData.securitiesBasket;
      basket.splice(securityIndex - 1, 1);

      // if the basket is empty, reset the sheet and add a new row to have a clean state
      if (isEmpty(basket)) {
        syncSecurityTargetValueLinkage(tableData, basket.length);
        await securitySheet.reset({
          tableData,
          rowConfig: getRowConfig(basket),
        });
        await addSecurity();
        resetConfiguration?.();
        return;
      }
      // if not, recalculate equity value based on new basket but same target value
      // get distribution for new equity value
      const { newDistribution } = (await recalculateEquityValue(selectedCapTable as CapTable, basket, tv)) as any;
      const methods = getAppliedMethodologies();
      const newPresentValues = newDistribution?.map((dist: any) => dist.presentValues);
      const totalPresentShareValues = calculateWeightedPerShareValues(newPresentValues, methods);

      const basketToSet = basket.map((row: any) => {
        const perShare = totalPresentShareValues?.find(
          ({ securityId }: any) => Number(securityId) === Number(row.security)
        );
        return { ...row, per_share: perShare?.value };
      });
      // Keep securitiesBasket.basket in sync with the rowConfig passed to reset.
      tableData.securitiesBasket.basket = basketToSet;
      syncSecurityTargetValueLinkage(tableData, basketToSet.length);
      await securitySheet.reset({
        tableData,
        rowConfig: getRowConfig(basketToSet),
      });
      // reset() does not re-parse; evaluate so Target Value re-links when basket length returns to 1.
      await workbook.evaluateSpreadsheets(Object.values(spreadsheets).filter(Boolean));

      resetConfiguration?.();
    },
    [
      spreadsheets,
      workbook,
      recalculateEquityValue,
      selectedCapTable,
      getAppliedMethodologies,
      resetConfiguration,
      addSecurity,
    ]
  );

  const deleteAllocationMethod = useCallback(
    async (allocationMethodIndex: number) => {
      const { backsolveSheet } = spreadsheets;

      const { applied_methodologies: currentAllocationMethods } = (backsolveSheet.tableData as any).approach
        .valuations_approach_backsolve;
      // if it was stored I want to store the deleted id in "deleted_methodologies"
      if (currentAllocationMethods.length > 1) {
        const deletedMethod = currentAllocationMethods.splice(allocationMethodIndex - 1, 1);
        const deletedMethodId = deletedMethod[0].id;

        if (deletedMethodId) {
          (backsolveSheet.tableData as any).approach.valuations_approach_backsolve.deleted_methodologies.push(
            deletedMethodId
          );
        }

        updateMethodologies(currentAllocationMethods);

        backsolveSheet.reset({
          tableData: backsolveSheet.tableData,
          columns: [[], ...currentAllocationMethods],
        });
        resetConfiguration?.();
        setOpm(checkIfIsOpm());
        // Recalculate Equity Value when there's only 1 method left
        if (currentAllocationMethods.length === 1) {
          recalculateEquityValue(selectedCapTable as CapTable);
        }
      }
    },
    [checkIfIsOpm, resetConfiguration, spreadsheets, recalculateEquityValue, selectedCapTable]
  );

  // When a change is made to a ledger we need to save the values returned from that save.
  const onMarketAdjustmentLedgerChange = useCallback(
    (ledgerData: LedgerData) => {
      const { marketAdjustmentSheet } = spreadsheets;
      const changes: [ICell, string | number][] = [];
      changes.push(
        [marketAdjustmentSheet.cells!.AsOfDate, ledgerData.AsOfDate],
        [marketAdjustmentSheet.cells!.Metric, ledgerData.Metric],
        [marketAdjustmentSheet.cells!.MarketAdjustment, ledgerData.AppliedAdjustment]
      );
      changes.forEach(([cell, expr]) => {
        onChange(cell, expr, true);
      });
      // eslint-disable-next-line no-param-reassign
      approach.valuations_approach_backsolve.public_companies_status = ledgerData.compData.map((comp: any) => ({
        id: comp.id,
        public_company: comp.public_company ?? null,
        ltm_revenue: comp.ltm_revenue,
        ltm_ebitda: comp.ltm_ebitda,
        ntm_revenue: comp.ntm_revenue,
        ntm_ebitda: comp.ntm_ebitda,
        market_cap: comp.market_cap,
        enterprise_value: comp.enterprise_value,
        enabled: comp.enabled,
        reference_for_backsolve: comp.reference_for_backsolve,
        ticker_symbol: comp.identifier,
        cap_iq_id: comp.cap_iq_id,
        valuation_approach_backsolve: approach.valuations_approach_backsolve.id ?? null,
      }));
      const { savedPublicCompStatusIds } = marketAdjustmentSheet.tableData as any;
      const toPersistPublicCompStatusIds = Array.from(new Set(ledgerData.compData.map(comp => comp.id)));
      const toDeletePublicCompStatusIds = Array.from(
        difference(savedPublicCompStatusIds, toPersistPublicCompStatusIds)
      );
      // eslint-disable-next-line no-param-reassign
      approach.valuations_approach_backsolve.deleted_public_company_status_ids = Array.from(
        new Set([
          ...(approach.valuations_approach_backsolve.deleted_public_company_status_ids ?? []),
          ...toDeletePublicCompStatusIds,
        ])
      );

      // also we store some of the other data from the ledger
      const otherUpdates = {
        percentile_selection_a: ledgerData.percentile_selection_a_selection,
        percentile_selection_b: ledgerData.percentile_selection_b_selection,
        adjustment_selected: ledgerData.SelectedAdjustment,
        specified_adjustment: ledgerData.SpecifiedAdjustment,
      };
      // eslint-disable-next-line no-param-reassign
      approach.valuations_approach_backsolve = {
        ...approach.valuations_approach_backsolve,
        ...otherUpdates,
      } as unknown as ValuationApproachBackSolveExtended;
      setMarketAdjustment(ledgerData.AppliedAdjustment);
    },
    [approach, onChange, spreadsheets]
  );

  const backsolveContextData = useMemo(
    () =>
      ({
        gpcApproach,
        securityList,
        spreadsheets,
        approach,
        onMarketAdjustmentLedgerChange,
        measurementDate,
        format,
        formatDispatch,
      }) as unknown as BacksolveContextTypes,
    [
      gpcApproach,
      securityList,
      spreadsheets,
      approach,
      onMarketAdjustmentLedgerChange,
      measurementDate,
      format,
      formatDispatch,
    ]
  );

  // Both arrive after the sheets are built: the security names come from the cap table fetch
  // (SecuritySelectionViewer renders a blank until then) and the OPM table fetches the risk free
  // rate after it mounts (the evaluated cell reads 0 until then). Neither may be captured early.
  const isSecurityListPending = Boolean(selectedCapTable) && securityList === undefined;
  const opmInputCells = spreadsheets.opmInputSheet?.cells;
  const opmBacksolveDate = opmInputCells?.OPM_BACKSOLVE_DATE?.value;
  const opmMaturity = opmInputCells?.MATURITY?.value;
  const isRiskFreeRatePending
    = isOpm
    && Boolean(opmBacksolveDate && opmMaturity && opmInputCells?.RISK_FREE_RATE)
    && !opmInputCells?.RISK_FREE_RATE?.value
    && emptyRiskFreeRateKey !== getRiskFreeRateKey(opmMaturity, opmBacksolveDate);

  let backsolveExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading || isLoadingMarketAdjustment || isEmpty(spreadsheets)) {
    backsolveExportStatus = 'loading';
  } else if (isSecurityListPending || isRiskFreeRatePending) {
    backsolveExportStatus = 'loading';
  } else if (approach?.valuations_approach_backsolve?.securities_basket?.basket === undefined) {
    backsolveExportStatus = 'loading';
  } else if (isEmpty(securityRows)) {
    backsolveExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.BACKSOLVE.id,
    status: backsolveExportStatus,
  });

  return (
    <>
      <CustomContainer data-ref={PdfExportTargets.BACKSOLVE.id} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
        <BacksolveContext.Provider value={backsolveContextData}>
          {/* Source Rows Table (Secondary Transactions / Mutual Fund Marks only) */}
          {isSourceRowsEnabled && spreadsheets.sourceRowsSheet && (
            <CustomCompactContainer>
              <Grid size={{ xs: 12 }}>
                <SourceRowsTable spreadsheets={spreadsheets} onChange={onChange} workbook={workbook} />
              </Grid>
              {!isExportMode && (
                <Grid size={{ xs: 12 }}>
                  <AddActionButton
                    onClickHandler={addSourceDataRow}
                    text="Add Source Data"
                    disabled={isDisabled}
                  />
                </Grid>
              )}
            </CustomCompactContainer>
          )}

          {/* Backsolve */}
          <CustomCompactContainer
            className={clsx(VALUATION_BACKSOLVE_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}>
            {/* Backsolve */}
            <Grid size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
              <BacksolveTable
                deleteColFn={deleteAllocationMethod}
                format={format}
                isLoading={isLoading}
                onChange={onChange}
                spreadsheets={spreadsheets}
                workbook={workbook}
              />
            </Grid>

            {/* Add allocation method */}
            {!isExportMode && (
              <Grid size={{ xs: 12 }}>
                <AddActionButton
                  onClickHandler={addAllocationMethod}
                  text="Add Allocation Method"
                  disabled={allocationMethods.length >= SCENARIO_METHODS_BACKSOLVE.length || isDisabled}
                />
              </Grid>
            )}
          </CustomCompactContainer>

          {/* Securities */}
          <CustomCompactContainer
            className={clsx(VALUATION_BACKSOLVE_SECURITES_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}>
            {/* Securities */}
            <Grid size={{ xs: 12 }}>
              <SecurityTable
                spreadsheets={spreadsheets}
                onChange={onChange}
                setPrevEditableSecurityValues={setPrevEditableSecurityValues}
                securities={securityRows}
                securityList={securityList}
                deleteRowFn={deleteSecurity}
                workbook={workbook}
                format={format}
              />
            </Grid>

            {/* Add row */}
            {!isExportMode && (
              <Grid size={{ xs: 12 }}>
                <AddActionButton
                  onClickHandler={addSecurity}
                  disabled={
                    securityRows.length >= getValidSecurities(selectedCapTable as unknown as CapTableInfo).length ||
                  isDisabled
                  }
                  text="Add Row"
                />
              </Grid>
            )}
          </CustomCompactContainer>

          {/* OPM */}
          {isOpm && (
            <Grid
              className={clsx(VALUATION_BACKSOLVE_OPM_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
              size={{ xs: 12 }}>
              <OPMInputTable
                componentId="approach-opm-input-table"
                spreadsheets={spreadsheets}
                onChange={onChange}
                setPrevOpmData={setPrevOpmData}
                fullWidth={false}
                workbook={workbook}
                measurementDate={measurementDate as MeasurementDate}
                onEmptyRiskFreeRateResolved={setEmptyRiskFreeRateKey}
              />
            </Grid>
          )}

          {/* Backsolve summary */}
          <Grid
            className={clsx(VALUATION_BACKSOLVE_SUMMARY_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
            size={{ xs: 12 }}>
            <BacksolveSummaryTable
              spreadsheets={spreadsheets}
              onChange={onChange}
              format={format}
              formatDispatch={formatDispatch}
              workbook={workbook}
            />
          </Grid>

          {/* Market adjustment — hidden from main sheet in export mode when it has its own sheet */}
          {!(isExportMode && hasMarketAdjustment) && (
            <CustomCompactContainer
              className={clsx(VALUATION_BACKSOLVE_MARKET_ADJUSTMENT_CONTAINER_CLASS, {
                'is-export-mode': isExportMode,
              })}>
              {!viewOnlyUser && !isExportMode && (
                <Grid size={{ xs: 12 }}>
                  <GridButtonWithTooltip
                    buttonText={
                      hasMarketAdjustment ? VALUATIONS_MARKET_ADJUSTMENT_REMOVE : VALUATIONS_MARKET_ADJUSTMENT_ADD
                    }
                    className="btn-table-aligned"
                    disabled={!hasGpcApproach || !approach.id}
                    icon={hasMarketAdjustment ? <RemoveIcon /> : <AddIcon />}
                    onClick={changeMarketAdjustment}
                    tooltipText={!hasGpcApproach || !approach.id ? MARKET_ADJUSTMENT_REQUIREMENT : ''}
                  />
                </Grid>
              )}

              {/* Market adjustment */}
              <Grid size={{ xs: 12 }}>
                {hasMarketAdjustment && (
                  <MarketAdjustmentTable
                    spreadsheets={spreadsheets}
                    onChange={onChange}
                    format={format}
                    formatDispatch={formatDispatch}
                    workbook={workbook}
                    isLoadingMarketAdjustment={isLoadingMarketAdjustment}
                  />
                )}
              </Grid>
            </CustomCompactContainer>
          )}

          <WidgetDrawer
            applicationContext={WidgetDrawerApplicationContext.BACKSOLVE}
            notesProps={{
              pageType: VALUATIONS_PAGE_VALUE,
              pageTypeKey: VALUATIONS_PAGE_KEY,
              pageTypeId: approach.id,
              notes: cachedNotes,
              isApproach: true,
              setNotes,
              onAddNote,
              onUpdateNotes,
              onDeleteNote,
              isDisabled,
            }}
          />
        </BacksolveContext.Provider>
      </CustomContainer>

      {/* Market Adjustment Ledger sheet — rendered as a separate capture target in export mode */}
      {isExportMode && hasMarketAdjustment && (
        <CustomContainer
          data-ref={PdfExportTargets.BACKSOLVE_MARKET_ADJUSTMENT_LEDGER.id}
          style={EXPORT_MODE_STYLES}>
          <BacksolveContext.Provider value={backsolveContextData}>
            <Grid
              className={clsx(VALUATION_BACKSOLVE_MARKET_ADJUSTMENT_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
              size={{ xs: 12 }}>
              <MarketAdjustmentTable
                spreadsheets={spreadsheets}
                onChange={onChange}
                format={format}
                formatDispatch={formatDispatch}
                workbook={workbook}
                isLoadingMarketAdjustment={isLoadingMarketAdjustment}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MarketAdjustmentLedger />
            </Grid>
          </BacksolveContext.Provider>
        </CustomContainer>
      )}
    </>
  );
};

export default Backsolve;
